// Live preview of the customer-facing loyalty surfaces, driven by the
// admin's draft "card appearance" values. Two mockups side by side:
//
//   1. The scanned screen — a faithful replica of the server-rendered
//      walletLandingPage (wallet_enroll.go): logo, headline, big progress,
//      stamp dots, sub copy, and the two "Add to Wallet" buttons. This page
//      is still driven by the loyalty ACCENT, not the card colours.
//   2. The wallet pass — driven by the three card colours, with a toggle
//      between how Apple and Google actually render them.
//
// Keep the structure aligned with walletLandingPage so the preview doesn't
// drift from what actually renders. All values are already merged (override
// → receipt fallback) by the caller.
//
// The pass palette comes from lib/walletCardPalette.ts, the shared mirror of
// backend/internal/wallet/palette.go — do NOT re-derive colours here.
import { useState } from 'react'
import { deriveCardPalette, luma } from '@/lib/walletCardPalette'

// A fixed faux-QR pattern (deterministic; no randomness).
const QR_PATTERN = [
  1, 1, 1, 0, 1, 1, 0, 1, 0, 1, 1, 1, 0, 1, 1, 0, 1, 1, 0, 1, 1, 0, 1, 1, 1,
]

interface LoyaltyScreenPreviewProps {
  /** The loyalty PAGE accent — drives mockup 1 only. Valid 6-digit hex. */
  accent: string
  /** The three wallet-CARD colours. Blank = derive from `accent` exactly as
   *  the card did before the colours were separable. */
  cardBackground?: string
  cardText?: string
  cardCircles?: string
  /** The wallet-pass mark (mockup 2). */
  logoUrl?: string
  /** The web-page logo (mockup 1). Omitted = falls back to logoUrl, which
   *  mirrors the server's mergeLoyaltyBranding precedence. */
  pageLogoUrl?: string
  /** Banner drawn behind the stamp coins. Mirrors PassState.HeroURL. */
  heroUrl?: string
  headline: string
  subtext?: string
  businessName: string
  balance: number
  threshold: number
  rewardLabel: string
}

export function LoyaltyScreenPreview({
  accent,
  cardBackground = '',
  cardText = '',
  cardCircles = '',
  logoUrl,
  pageLogoUrl,
  heroUrl,
  headline,
  subtext,
  businessName,
  balance,
  threshold,
  rewardLabel,
}: LoyaltyScreenPreviewProps) {
  const [platform, setPlatform] = useState<'apple' | 'google'>('apple')

  // The page mockup uses the page logo; the pass mockup always uses logoUrl.
  const pageLogo = pageLogoUrl || logoUrl
  const safeThreshold = Math.max(1, threshold)
  const safeBalance = Math.max(0, Math.min(balance, safeThreshold))
  const remaining = safeThreshold - safeBalance

  // Mirrors walletLandingPage's sub-copy logic, overridden by the admin's
  // subtext when set.
  const autoSub =
    remaining > 0
      ? `${remaining} more stamp${remaining === 1 ? '' : 's'} to ${rewardLabel || 'your reward'}`
      : 'Reward unlocked! 🎉'
  const sub = subtext && subtext.trim() ? subtext : autoSub

  // Cap the rendered dots so a large threshold doesn't blow out the frame.
  const dotCount = Math.min(safeThreshold, 12)
  const dots = Array.from({ length: dotCount }, (_, i) => i < safeBalance)

  const pal = deriveCardPalette({
    background: cardBackground,
    text: cardText,
    circles: cardCircles,
    legacyAccent: accent,
  })

  // Google's loyalty class takes hexBackgroundColor and then picks the on-card
  // text colour ITSELF for contrast — hexFontColor is sent but may be ignored.
  // Showing the tenant's text colour on the Google mockup would be a promise
  // we can't keep, so the Google view renders Google's own likely choice.
  const googleText = pal.light ? '#0b0a07' : '#ffffff'
  const passText = platform === 'apple' ? pal.text : googleText
  const passLabel = platform === 'apple' ? pal.label : googleText

  const coinCount = Math.min(safeThreshold, 8)
  const coins = Array.from({ length: coinCount }, (_, i) =>
    i < safeBalance ? 'on' : i === safeBalance && remaining > 0 ? 'next' : 'empty',
  )

  return (
    <div className="flex flex-wrap gap-4">
      {/* 1. Scan screen (phone frame) — still driven by the page accent. */}
      <div className="w-[190px] shrink-0 rounded-[26px] border-[6px] border-stone-800 bg-stone-800 shadow-md">
        <div className="rounded-[20px] bg-[#faf7f2] px-4 py-5 text-center">
          {pageLogo ? (
            <img src={pageLogo} alt="" className="mx-auto mb-2 max-h-9 max-w-[60%] object-contain" />
          ) : null}
          <div className="truncate text-[14px] font-semibold text-stone-900">{headline || 'Loyalty rewards'}</div>
          <div className="mt-2 text-[30px] font-bold leading-none" style={{ color: accent }}>
            {safeBalance} of {safeThreshold}
          </div>
          <div className="mt-3 flex flex-wrap justify-center gap-1.5">
            {dots.map((on, i) => (
              <span
                key={i}
                className="h-3 w-3 rounded-full border-2"
                style={{ borderColor: accent, background: on ? accent : 'transparent', opacity: on ? 1 : 0.4 }}
              />
            ))}
          </div>
          <div className="mt-2 min-h-[16px] text-[11px] leading-tight text-stone-500">{sub}</div>
          <div className="mt-3 space-y-1.5">
            <div className="rounded-lg bg-stone-900 py-2 text-[11px] font-semibold text-white"> Add to Apple Wallet</div>
            <div className="rounded-lg border border-stone-900 py-2 text-[11px] font-semibold text-stone-900"> Add to Google Wallet</div>
          </div>
        </div>
      </div>

      {/* 2. Wallet pass mockup */}
      <div className="w-[190px] shrink-0 self-start">
        <div className="mb-1.5 flex rounded-md border border-border/70 p-0.5 text-[10px] font-medium">
          {(['apple', 'google'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPlatform(p)}
              aria-pressed={platform === p}
              className={
                'flex-1 rounded-[3px] py-1 capitalize transition-colors ' +
                (platform === p ? 'bg-muted font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground')
              }
            >
              {p === 'apple' ? 'Apple' : 'Google'}
            </button>
          ))}
        </div>

        {/* Apple's backgroundColor is a FLAT fill — no gradient. This mockup
            used to draw one, which meant the preview showed a card the real
            pass never rendered. */}
        <div
          className="overflow-hidden rounded-xl shadow-md"
          style={{ background: pal.bg, color: passText }}
        >
          <div className="flex items-center gap-2 px-3 pt-3">
            <div
              className="grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-[7px]"
              style={{ background: pal.panel, boxShadow: `inset 0 0 0 1px ${pal.stroke}40` }}
            >
              {logoUrl ? (
                <img src={logoUrl} alt="" className="h-full w-full object-contain p-[3px]" />
              ) : (
                <span className="h-2 w-2 rounded-full" style={{ background: pal.circles }} />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[7px] font-bold uppercase tracking-[0.2em]" style={{ color: passLabel }}>
                Loyalty card
              </div>
              <div className="truncate text-[12px] font-semibold leading-tight">{businessName || 'Loyalty'}</div>
            </div>
            <div className="ml-auto shrink-0 text-right">
              <div className="text-[7px] font-bold uppercase tracking-[0.15em]" style={{ color: passLabel }}>
                Stamps
              </div>
              <div className="text-[12px] font-bold leading-none">
                {safeBalance}/{safeThreshold}
              </div>
            </div>
          </div>

          {/* Strip band of stamp coins — over the tenant banner when set. The
              scrim comes from the shared palette and follows the card: black
              on a dark card, white on a light one, matching passimage.go's
              heroScrimAlpha / heroScrimAlphaLight. */}
          <div
            className="mx-2 mt-2 rounded-lg px-2.5 py-2.5"
            style={
              heroUrl
                ? {
                    backgroundImage: `linear-gradient(${pal.scrim}, ${pal.scrim}), url(${JSON.stringify(heroUrl)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }
                : { background: pal.panel }
            }
          >
            <div className="flex flex-wrap gap-1.5">
              {coins.map((s, i) => (
                <span
                  key={i}
                  className="h-[18px] w-[18px] rounded-full"
                  style={
                    s === 'on'
                      ? {
                          background: `radial-gradient(circle at 35% 30%, ${pal.circlesHi}, ${pal.circles})`,
                          boxShadow: `0 1px 3px ${pal.coinShadow}`,
                        }
                      : s === 'next'
                        ? { boxShadow: `inset 0 0 0 2px ${pal.stroke}` }
                        : { boxShadow: `inset 0 0 0 1.5px ${pal.stroke}`, opacity: 0.32 }
                  }
                />
              ))}
            </div>
          </div>

          <div className="flex items-end justify-between px-3 pb-1 pt-2">
            <div className="min-w-0">
              <div className="text-[7px] font-bold uppercase tracking-[0.15em]" style={{ color: passLabel }}>
                Reward
              </div>
              <div className="truncate text-[12px] font-medium">{rewardLabel || 'Reward'}</div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-[7px] font-bold uppercase tracking-[0.15em]" style={{ color: passLabel }}>
                To go
              </div>
              <div className="text-[12px] font-medium">{remaining > 0 ? remaining : '✓'}</div>
            </div>
          </div>

          <div className="flex justify-center pb-3 pt-1.5">
            <div className="grid grid-cols-5 gap-[2px] rounded-md bg-white p-1.5">
              {QR_PATTERN.map((on, i) => (
                <span
                  key={i}
                  className="h-[5px] w-[5px] rounded-[1px]"
                  style={{ background: on ? '#0c0a08' : 'transparent' }}
                />
              ))}
            </div>
          </div>
        </div>

        <p className="mt-1.5 text-[10px] leading-tight text-muted-foreground">
          {platform === 'apple'
            ? 'Apple Wallet uses all three colours.'
            : `Google picks its own text colour (${luma(pal.bg) >= 140 ? 'dark' : 'white'}) from your background. Your text and circle colours still reach Android through the card artwork.`}
        </p>
      </div>
    </div>
  )
}
